import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useGlossaryBlueprintsData } from "@/lib/useTemplatesData";
import { getCachedObjects, FormFieldObject, sectionDataObject } from "@/lib/stores/objectsStore";
import { getFolders } from "@/lib/stores/folderStore";
import { Folder, Link2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface TextLinkWizardProps {
  isOpen: boolean;
  onClose: () => void;
  selectedText: string;
  onCreateLibraryObject: (data: {
    templateId: string;
    folderId: string | null;
    name: string;
    selectedText: string;
  }) => void;
  onLinkToValue: (data: {
    objectId: string;
    fieldId: string;
    selectedText: string;
  }) => void;
}

type WizardStep = "select-option" | "create-object" | "link-value";

export const TextLinkWizard = ({
  isOpen,
  onClose,
  selectedText,
  onCreateLibraryObject,
  onLinkToValue,
}: TextLinkWizardProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const { glossaryBlueprints } = useGlossaryBlueprintsData();

  const [step, setStep] = useState<WizardStep>("select-option");
  
  // Create Object state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [objectName, setObjectName] = useState("");
  const [folders, setFolders] = useState<any[]>([]);

  // Link to Value state
  const [selectedObjectId, setSelectedObjectId] = useState<string>("");
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [libraryObjects, setLibraryObjects] = useState<any[]>([]);
  const [availableFields, setAvailableFields] = useState<{
    field: FormFieldObject;
    sectionTitle: string;
    isAvailable: boolean;
  }[]>([]);

  // Load folders when game changes
  useEffect(() => {
    if (selectedGame?.id && step === "create-object") {
      loadFolders();
    }
  }, [selectedGame?.id, step]);

  // Load library objects when game changes
  useEffect(() => {
    if (selectedGame?.id && step === "link-value") {
      loadLibraryObjects();
    }
  }, [selectedGame?.id, step]);

  // Update available fields when object is selected
  useEffect(() => {
    if (selectedObjectId && libraryObjects.length > 0) {
      updateAvailableFields();
    }
  }, [selectedObjectId, libraryObjects]);

  const loadFolders = async () => {
    if (!selectedGame?.id) return;
    try {
      const loadedFolders = await getFolders(selectedGame.id, "library");
      setFolders(loadedFolders || []);
    } catch (error) {
      console.error("Error loading folders:", error);
      setFolders([]);
    }
  };

  const loadLibraryObjects = () => {
    if (!selectedGame?.id) return;
    try {
      const objects = getCachedObjects(selectedGame.id);
      setLibraryObjects(objects || []);
    } catch (error) {
      console.error("Error loading library objects:", error);
      setLibraryObjects([]);
    }
  };

  const updateAvailableFields = () => {
    const selectedObject = libraryObjects.find(obj => obj.id === selectedObjectId);
    if (!selectedObject || !selectedObject.data) {
      setAvailableFields([]);
      return;
    }

    const fields: {
      field: FormFieldObject;
      sectionTitle: string;
      isAvailable: boolean;
    }[] = [];

    selectedObject.data.forEach((section: sectionDataObject) => {
      section.fields.forEach((field: FormFieldObject) => {
        // Check if field is available for linking
        const isAvailable = isFieldAvailableForLinking(field);
        fields.push({
          field,
          sectionTitle: section.title,
          isAvailable,
        });
      });
    });

    setAvailableFields(fields);
  };

  const isFieldAvailableForLinking = (field: FormFieldObject): boolean => {
    // Only allow linking to text fields (title, textarea, text)
    const textFieldTypes = ["title", "textarea", "text"];
    if (!textFieldTypes.includes(field.type)) {
      return false;
    }

    // Check if field is empty - consider null, undefined, empty string, or whitespace-only strings as empty
    const isEmpty = field.value === null || 
                   field.value === undefined || 
                   field.value === '' ||
                   (typeof field.value === 'string' && field.value.trim() === '');

    // Check if field allows multiple values
    const allowsMultiple = field.multiple;

    // Field is available if it's empty OR allows multiple values
    return isEmpty || allowsMultiple;
  };

  const handleOptionSelect = (option: "create" | "link") => {
    if (option === "create") {
      setStep("create-object");
    } else {
      setStep("link-value");
    }
  };

  const handleBack = () => {
    setStep("select-option");
    // Reset state
    setSelectedTemplateId("");
    setSelectedFolderId(null);
    setObjectName("");
    setSelectedObjectId("");
    setSelectedFieldId("");
  };

  const handleCreateObject = () => {
    if (!selectedTemplateId || !objectName.trim()) {
      return;
    }

    onCreateLibraryObject({
      templateId: selectedTemplateId,
      folderId: selectedFolderId,
      name: objectName.trim(),
      selectedText,
    });
    onClose();
  };

  const handleLinkToValue = () => {
    if (!selectedObjectId || !selectedFieldId) {
      return;
    }

    onLinkToValue({
      objectId: selectedObjectId,
      fieldId: selectedFieldId,
      selectedText,
    });
    onClose();
  };

  const handleClose = () => {
    // Reset all state
    setStep("select-option");
    setSelectedTemplateId("");
    setSelectedFolderId(null);
    setObjectName("");
    setSelectedObjectId("");
    setSelectedFieldId("");
    onClose();
  };

  // Get published templates only
  const publishedTemplates = glossaryBlueprints?.filter(bp => bp.status === 'published') || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">
            {step === "select-option" && "Link Selected Text"}
            {step === "create-object" && "Create Library Object"}
            {step === "link-value" && "Link to Value"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Selected Text Display */}
          <div className="bg-muted p-3 rounded-md">
            <p className="text-sm font-medium mb-1 text-center">Selected Text</p>
            <p className="text-sm text-muted-foreground italic text-center">"{selectedText}"</p>
          </div>

          {/* Step: Select Option */}
          {step === "select-option" && (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleOptionSelect("create")}
                className={cn(
                  "flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-2 transition-all",
                  "hover:border-primary hover:bg-primary/5",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                )}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Plus className="w-6 h-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-medium">Create Library Object</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a new object from a template
                  </p>
                </div>
              </button>

              <button
                onClick={() => handleOptionSelect("link")}
                className={cn(
                  "flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-2 transition-all",
                  "hover:border-primary hover:bg-primary/5",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                )}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-medium">Link to Value</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Link to an existing object field
                  </p>
                </div>
              </button>
            </div>
          )}

          {/* Step: Create Object */}
          {step === "create-object" && (
            <div className="space-y-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Template <span className="text-red-500">*</span>
                </label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {publishedTemplates.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No templates available
                      </div>
                    ) : (
                      publishedTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Object Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Object Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={objectName}
                  onChange={(e) => setObjectName(e.target.value)}
                  placeholder="Enter object name"
                />
              </div>

              {/* Folder Selection (Optional) */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Folder className="w-4 h-4" />
                  Folder (Optional)
                </label>
                <Select
                  value={selectedFolderId || "root"}
                  onValueChange={(value) => setSelectedFolderId(value === "root" ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select folder (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">Root (No Folder)</SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  If left blank, the object will be placed in the root folder
                </p>
              </div>
            </div>
          )}

          {/* Step: Link to Value */}
          {step === "link-value" && (
            <div className="space-y-4">
              {/* Object Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Select Object <span className="text-red-500">*</span>
                </label>
                <Select value={selectedObjectId} onValueChange={setSelectedObjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an object" />
                  </SelectTrigger>
                  <SelectContent>
                    {libraryObjects.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No library objects available
                      </div>
                    ) : (
                      libraryObjects.map((object) => (
                        <SelectItem key={object.id} value={object.id}>
                          {object.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Field Selection */}
              {selectedObjectId && availableFields.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Select Field <span className="text-red-500">*</span>
                  </label>
                  <Select value={selectedFieldId} onValueChange={setSelectedFieldId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a field" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFields.map(({ field, sectionTitle, isAvailable }) => (
                        <SelectItem
                          key={field.id}
                          value={field.id}
                          disabled={!isAvailable}
                          className={cn(
                            !isAvailable && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{field.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({sectionTitle})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only empty text fields or fields that allow multiple values are available
                  </p>
                </div>
              )}

              {selectedObjectId && availableFields.length === 0 && (
                <div className="p-4 bg-muted rounded-md text-center">
                  <p className="text-sm text-muted-foreground">
                    No available fields found in this object
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div>
            {step !== "select-option" && (
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {step === "create-object" && (
              <Button
                onClick={handleCreateObject}
                disabled={!selectedTemplateId || !objectName.trim()}
              >
                Create Object
              </Button>
            )}
            {step === "link-value" && (
              <Button
                onClick={handleLinkToValue}
                disabled={!selectedObjectId || !selectedFieldId}
              >
                Link to Field
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

